import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { playerApi } from '../lib/api';

interface Preset {
  id: number;
  name: string;
  mood: string | null;
  genres: string[];
  createdAt: number;
  updatedAt: number;
}

interface Props {
  token: string;
  selectedMood: string | null;
  selectedGenres: string[];
  onLoad: (mood: string | null, genres: string[]) => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export default function PresetSelector({
  token,
  selectedMood,
  selectedGenres,
  onLoad,
  onSuccess,
  onError,
}: Props) {
  const { t } = useTranslation();
  const [presets, setPresets] = useState<Preset[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveMode, setIsSaveMode] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [confirmOverwrite, setConfirmOverwrite] = useState<{
    existingId: number;
    name: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const saveInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const fetchPresets = useCallback(async () => {
    try {
      const data = await playerApi.listPresets(token);
      setPresets(data);
    } catch {
      // silent fail on load
    }
  }, [token]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  useEffect(() => {
    if (isSaveMode && saveInputRef.current) {
      saveInputRef.current.focus();
    }
  }, [isSaveMode]);

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setIsSaveMode(false);
        setEditingId(null);
        setConfirmOverwrite(null);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSave = async () => {
    const name = saveName.trim();
    if (!name) {
      onError(t('presetNameRequired'));
      return;
    }
    if (name.length > 10) {
      onError(t('presetNameTooLong'));
      return;
    }

    setLoading(true);
    try {
      const result = await playerApi.createPreset(token, {
        name,
        mood: selectedMood,
        genres: selectedGenres,
      });

      if ('error' in result && result.error === 'duplicate') {
        setConfirmOverwrite({ existingId: result.existingId, name });
        setLoading(false);
        return;
      }

      await fetchPresets();
      setSaveName('');
      setIsSaveMode(false);
      onSuccess(t('presetSaved'));
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('error');
      if (msg.includes('Maximum')) {
        onError(t('presetLimitReached'));
      } else {
        onError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOverwrite = async () => {
    if (!confirmOverwrite) return;

    setLoading(true);
    try {
      await playerApi.overwritePreset(token, confirmOverwrite.existingId, {
        mood: selectedMood,
        genres: selectedGenres,
      });
      await fetchPresets();
      setSaveName('');
      setIsSaveMode(false);
      setConfirmOverwrite(null);
      onSuccess(t('presetSaved'));
    } catch (e) {
      onError(e instanceof Error ? e.message : t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLoad = (preset: Preset) => {
    onLoad(preset.mood, preset.genres);
    setIsOpen(false);
    onSuccess(t('presetLoaded'));
  };

  const handleRename = async (id: number) => {
    const name = editingName.trim();
    if (!name) {
      onError(t('presetNameRequired'));
      return;
    }
    if (name.length > 10) {
      onError(t('presetNameTooLong'));
      return;
    }

    setLoading(true);
    try {
      const result = await playerApi.updatePresetName(token, id, name);
      if ('error' in result && result.error === 'duplicate') {
        onError(t('presetDuplicateName'));
        setLoading(false);
        return;
      }
      await fetchPresets();
      setEditingId(null);
      setEditingName('');
      onSuccess(t('presetRenamed'));
    } catch (e) {
      onError(e instanceof Error ? e.message : t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    try {
      await playerApi.deletePreset(token, id);
      await fetchPresets();
      onSuccess(t('presetDeleted'));
    } catch (e) {
      onError(e instanceof Error ? e.message : t('error'));
    } finally {
      setLoading(false);
    }
  };

  const formatTags = (mood: string | null, genres: string[]) => {
    const parts: string[] = [];
    if (mood) parts.push(t(`tagNames.${mood}`, { defaultValue: mood }));
    genres.forEach((g) => parts.push(t(`tagNames.${g}`, { defaultValue: g })));
    return parts.join(', ') || '-';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          setIsSaveMode(false);
          setEditingId(null);
          setConfirmOverwrite(null);
        }}
        className="p-2 rounded-lg bg-white/5 border border-white/10 hover:border-[#FFFB93] transition-colors"
        title={t('presets')}
        aria-label={t('presets')}
      >
        <svg className="w-5 h-5 text-[#FFFB93]" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-72 rounded-xl shadow-xl z-50 overflow-hidden"
          style={{ background: 'rgba(4,11,19,0.98)', border: '1px solid rgba(255,251,147,0.2)' }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <span className="font-semibold text-white">{t('presets')}</span>
            <button
              onClick={() => {
                setIsSaveMode(true);
                setSaveName('');
                setConfirmOverwrite(null);
              }}
              className="text-xs px-3 py-1 rounded-full bg-[#FFFB93] text-[#040B13] font-medium hover:bg-[#FFFB93]/80 transition-colors"
            >
              {t('savePreset')}
            </button>
          </div>

          {/* Save form */}
          {isSaveMode && !confirmOverwrite && (
            <div className="px-4 py-3 border-b border-white/10">
              <div className="flex gap-2">
                <input
                  ref={saveInputRef}
                  type="text"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave();
                    if (e.key === 'Escape') {
                      setIsSaveMode(false);
                      setSaveName('');
                    }
                  }}
                  placeholder={t('presetName')}
                  maxLength={10}
                  className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/20 text-white text-sm placeholder-gray-500 focus:border-[#FFFB93] focus:outline-none"
                />
                <button
                  onClick={handleSave}
                  disabled={loading || !saveName.trim()}
                  className="px-3 py-2 rounded-lg bg-[#FFFB93] text-[#040B13] text-sm font-medium disabled:opacity-50 hover:bg-[#FFFB93]/80 transition-colors"
                >
                  {loading ? '...' : t('save')}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {formatTags(selectedMood, selectedGenres)}
              </p>
            </div>
          )}

          {/* Overwrite confirmation */}
          {confirmOverwrite && (
            <div className="px-4 py-3 border-b border-white/10 bg-[#FFFB93]/10">
              <p className="text-sm text-white mb-3">{t('confirmOverwrite')}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setConfirmOverwrite(null);
                    setSaveName('');
                  }}
                  className="flex-1 px-3 py-2 rounded-lg bg-white/10 text-white text-sm hover:bg-white/20 transition-colors"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleOverwrite}
                  disabled={loading}
                  className="flex-1 px-3 py-2 rounded-lg bg-[#FFFB93] text-[#040B13] text-sm font-medium disabled:opacity-50 hover:bg-[#FFFB93]/80 transition-colors"
                >
                  {loading ? '...' : t('overwrite')}
                </button>
              </div>
            </div>
          )}

          {/* Preset list */}
          <div className="max-h-64 overflow-y-auto">
            {presets.length === 0 ? (
              <div className="px-4 py-6 text-center text-gray-500 text-sm">{t('noPresets')}</div>
            ) : (
              presets.map((preset) => (
                <div
                  key={preset.id}
                  className="px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors"
                >
                  {editingId === preset.id ? (
                    <div className="flex gap-2">
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRename(preset.id);
                          if (e.key === 'Escape') {
                            setEditingId(null);
                            setEditingName('');
                          }
                        }}
                        maxLength={10}
                        className="flex-1 px-2 py-1 rounded bg-white/10 border border-white/20 text-white text-sm focus:border-[#FFFB93] focus:outline-none"
                      />
                      <button
                        onClick={() => handleRename(preset.id)}
                        disabled={loading || !editingName.trim()}
                        className="px-2 py-1 rounded bg-[#FFFB93] text-[#040B13] text-xs font-medium disabled:opacity-50"
                      >
                        {loading ? '...' : t('save')}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2">
                      <button
                        onClick={() => handleLoad(preset)}
                        className="flex-1 text-left min-w-0"
                      >
                        <div className="font-medium text-white truncate">{preset.name}</div>
                        <div className="text-xs text-gray-400 truncate mt-0.5">
                          {formatTags(preset.mood, preset.genres)}
                        </div>
                      </button>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => {
                            setEditingId(preset.id);
                            setEditingName(preset.name);
                          }}
                          className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                          title={t('rename')}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(preset.id)}
                          disabled={loading}
                          className="p-1.5 rounded hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                          title={t('delete')}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
